import { PropsWithChildren } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Head, Link } from '@inertiajs/react';
import { useAuth, useLocalizedRoute } from '@/hooks';
import { UI } from '@/Components/UI';
import Logo from '@/../img/logo.svg';
import Favicon from '@/../img/favicon.svg';
import Favicon32 from '@/../img/favicon-32.webp';
import Favicon512 from '@/../img/favicon-512.webp';
import { twMerge } from 'tailwind-merge';

type MenuItems = 'search' | 'collections' | 'profile' | 'home';

type AppLayoutProps = PropsWithChildren<{
    activeItem?: MenuItems;
    className?: string;
}>;

export const AppLayout = ({ activeItem, className, children }: AppLayoutProps) => {
    const { t } = useTranslation('app');
    const { localizedRoute } = useLocalizedRoute();
    const user = useAuth();

    return (
        <>
            <Head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta name="mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="color-scheme" content="light" />
                <meta name="application-name" content={import.meta.env.VITE_APP_NAME} />

                <link
                    rel="icon"
                    type="image/svg"
                    href={Favicon}
                />
                <link
                    rel="icon"
                    type="image/webp"
                    sizes="32x32"
                    href={Favicon32}
                />
                <link
                    rel="icon"
                    type="image/webp"
                    sizes="512x512"
                    href={Favicon512}
                />
                <link
                    rel="apple-touch-icon"
                    href={Favicon}
                />
                <link
                    rel="apple-touch-icon"
                    sizes="32x32"
                    href={Favicon32}
                />
                <link
                    rel="apple-touch-icon"
                    sizes="512x512"
                    href={Favicon512}
                />
            </Head>

            <div className={'flex flex-col min-h-screen w-full pb-24 md:pb-0 md:pl-28 lg:pl-64'}>
                <UI.Menu>
                    <Link
                        href={localizedRoute('home')}
                        className={'hidden md:flex md:flex-col md:items-center lg:mb-4'}>
                        <img
                            src={Logo as string}
                            alt={import.meta.env.VITE_APP_NAME}
                            className={'h-10 lg:h-14'} />

                        <span className={'hidden font-baloo text-4xl font-semibold lg:block'}>
                            {import.meta.env.VITE_APP_NAME}
                        </span>
                    </Link>

                    <UI.Menu.Item
                        href={localizedRoute('home')}
                        icon={'home'}
                        isActive={activeItem === 'home'}>
                        {t('app:dashboard')}
                    </UI.Menu.Item>

                    <UI.Menu.Item
                        href={localizedRoute('search.index')}
                        icon={'search'}
                        isActive={activeItem === 'search'}>
                        {t('search:title')}
                    </UI.Menu.Item>

                    <UI.Menu.Item
                        href={localizedRoute('recipeCollections.index')}
                        icon={'collections_bookmark'}
                        isActive={activeItem === 'collections'}>
                        {t('collections:title')}
                    </UI.Menu.Item>

                    <UI.Menu.Item
                        href={user ? localizedRoute('profile.show', { profile: user.slug }) : localizedRoute('login')}
                        icon={'person'}
                        className={'lg:mt-auto'}
                        isActive={activeItem === 'profile'}>
                        {t('profile:title')}
                    </UI.Menu.Item>
                </UI.Menu>

                <main className={twMerge('grow mx-auto w-full min-h-full max-w-4xl p-4', className)}>
                    {children}
                </main>

                <div className={'p-4 text-center text-sm text-on-background/50'}>
                    <Trans
                        i18nKey={'app:copyRight'}
                        values={{ year: new Date().getFullYear() }}
                        components={[
                            <></>,
                            <UI.Link
                                className={'text-unset'}
                                as={'a'}
                                target={'_blank'}
                                href={'https://erik.sadewater.com/'} />
                        ]} />

                    <br />

                    <UI.Link
                        className={'text-unset'}
                        href={localizedRoute('legal.imprint')}>
                        {t('legal:imprint.title')}
                    </UI.Link>

                    &thinsp;&bull;&thinsp;

                    <UI.Link
                        className={'text-unset'}
                        href={localizedRoute('legal.privacy')}>
                        {t('legal:privacy.title')}
                    </UI.Link>
                </div>
            </div>
        </>
    );
};
